import { computed, ref } from 'vue';

import { useApplicationBundles } from '@tallo/investor/applications';
import { useIntelligentEscalationsQuery } from '@tallo/investor/intelligent-escalations';
import { useAccessRequestsToAnswerQuery, useCompanyWantsToApplyQuery } from '@tallo/investor/property';
import { useShowingsQuery } from '@tallo/investor/showing';

import {
  AccessRequestItem,
  ActionNeeded,
  ActionNeededType,
  ApplicationItem,
  IntelligentEscalationItem,
  ShowingItem,
  WantsToApplyItem,
} from '../interfaces/actions-needed-item.interface';

/** When the item came to need the owner: an ask to apply by its date, as the wants-to-apply lists sort it */
const neededSince = (action: ActionNeeded) =>
  new Date(
    action.type === ActionNeededType.WantsToApply
      ? (action.item.applicationRequestedAt ?? action.item.createdAt)
      : action.item.createdAt,
  ).getTime();

export function useActionsNeeded() {
  const intelligentEscalationsQuery = useIntelligentEscalationsQuery();
  const intelligentEscalations = computed(() => intelligentEscalationsQuery.data?.value || []);

  const showingsQuery = useShowingsQuery({ onlyWithPendingShowingReqests: true });
  const pendingShowings = computed(() => showingsQuery.data.value || []);

  const applicationBundles = useApplicationBundles();

  // Colleagues asking to work on a unit the viewer can add them to
  const accessRequestsQuery = useAccessRequestsToAnswerQuery();
  const accessRequests = computed(() => accessRequestsQuery.data.value || []);

  // The same list as Home's Wants to apply tab, so the two never disagree: an ask the owner ignored or answered is
  // gone from both
  const wantsToApplyQuery = useCompanyWantsToApplyQuery();
  const wantsToApply = computed(() => wantsToApplyQuery.data.value || []);

  const isPending = computed(
    () =>
      showingsQuery.isPending.value ||
      intelligentEscalationsQuery.isPending.value ||
      applicationBundles.isPending.value ||
      accessRequestsQuery.isPending.value ||
      wantsToApplyQuery.isPending.value,
  );

  const isLoading = computed(
    () =>
      intelligentEscalationsQuery.isLoading.value ||
      applicationBundles.isLoading.value ||
      showingsQuery.isLoading.value ||
      accessRequestsQuery.isLoading.value ||
      wantsToApplyQuery.isLoading.value,
  );

  const actionsNeeded = computed(() => {
    const intelligentEscalationItems: IntelligentEscalationItem[] = intelligentEscalations.value.map((item) => ({
      type: ActionNeededType.IntelligentEscalation,
      item,
    }));

    const pendingShowingItems: ShowingItem[] = pendingShowings.value.map((item) => ({
      type: ActionNeededType.ShowingRequest,
      item,
    }));

    const applicationItems: ApplicationItem[] = applicationBundles.readyForReview.value.map((item) => ({
      type: ActionNeededType.Application,
      item,
    }));

    const accessRequestItems: AccessRequestItem[] = accessRequests.value.map((item) => ({
      type: ActionNeededType.AccessRequest,
      item,
    }));

    const wantsToApplyItems: WantsToApplyItem[] = wantsToApply.value.map((item) => ({
      type: ActionNeededType.WantsToApply,
      item,
    }));

    const data: ActionNeeded[] = [
      ...intelligentEscalationItems,
      ...pendingShowingItems,
      ...applicationItems,
      ...accessRequestItems,
      ...wantsToApplyItems,
    ];

    return data.sort((a, b) => neededSince(b) - neededSince(a));
  });

  return {
    data: actionsNeeded,
    isLoading,
    isPending,
  };
}
